import { tailwindColorsPalette } from './color';
const navy = tailwindColorsPalette.primary;
const gold = tailwindColorsPalette.gold;
/** CSS variable prefix for every Atlas token: `--atlas-<name>`. */
export const ATLAS_CSS_VAR_PREFIX = '--atlas-';
const lightColors = {
    // Surfaces and lines
    bg: '#F6F7F9',
    surface: '#FFFFFF',
    'surface-2': '#FAFAFB',
    line: tailwindColorsPalette.gray[100], // #E9EAEB
    'line-strong': tailwindColorsPalette.gray[200], // #D5D7DA
    // Ink (text): 1 = primary, 2 = secondary, 3 = meta / muted
    'ink-1': tailwindColorsPalette.gray[900], // #181D27
    'ink-2': '#414651',
    'ink-3': tailwindColorsPalette.gray[600], // #535862; WP-51 AA on muted/tinted surfaces
    // Accent (navy family). `accent` is for text/links, `primary` for filled controls.
    accent: navy[600], // #004790
    'accent-soft': navy[50], // #E6EEF7
    'accent-strong': navy[600], // greeting emphasis, hover ink on chips
    'accent-line': navy[100], // #CCDDEF, active Atlas row border
    primary: navy[600], // #004790
    'primary-hover': navy[700], // #003D7A
    'on-primary': '#FFFFFF',
    'user-bubble': navy[600],
    'user-ink': '#FFFFFF',
    focus: navy[400], // #3377BF
    // Gold: Atlas mark and Needs-you only
    gold: gold[500], // #D4A84B
    'gold-soft': gold[50], // #FDF8E9, needs-you row wash
    'gold-muted': gold[100], // #FAF0D3, needs-you tag / risk badge fill
    'gold-ink': gold[800], // #7C622A (prototype used 700 = 3.55:1 on gold-muted; 800 passes AA at 5.08:1)
    'on-gold': '#2B1F05', // text on a solid gold count badge
    // Status (text + tinted background)
    ok: tailwindColorsPalette.success[700], // #027A48
    'ok-bg': tailwindColorsPalette.success[50], // #ECFDF3
    warn: tailwindColorsPalette.warning[700], // #B54708
    'warn-bg': tailwindColorsPalette.warning[50], // #FFFAEB
    bad: tailwindColorsPalette.danger[700], // #B42318
    'bad-bg': tailwindColorsPalette.danger[50], // #FEF3F2
    // Status dots (always paired with a text label; never color-only)
    'dot-ok': tailwindColorsPalette.success[500], // #12B76A
    'dot-work': navy[400], // #3377BF
    'dot-need': gold[500], // #D4A84B
    'dot-bad': tailwindColorsPalette.danger[500], // #F04438
    'dot-idle': tailwindColorsPalette.gray[500], // #717680
    // Code / embed snippet (dark block in both schemes)
    'code-bg': navy[950], // #001A33
    'code-ink': '#CFE0F5',
    'code-string': navy[200], // #99BBDF (prototype used gold-300; gold is reserved for mark + Needs-you)
    // Elevation
    'shadow-sm': '0 1px 2px rgba(16,24,40,.05)',
    'shadow-md': '0 1px 2px rgba(16,24,40,.04), 0 8px 24px -8px rgba(0,41,82,.12)',
    scrim: 'rgba(0,26,51,.35)',
};
const darkColors = {
    bg: '#07111E',
    surface: '#0C1A2B',
    'surface-2': '#0F2034',
    line: '#1A2C42',
    'line-strong': '#27405E',
    'ink-1': '#EEF2F7',
    'ink-2': '#C3CCD8',
    'ink-3': '#8A98AB',
    accent: '#7FA9D9',
    'accent-soft': '#11284A',
    'accent-strong': '#9CC0EA',
    'accent-line': '#1F3E66',
    primary: navy[600],
    'primary-hover': navy[700],
    'on-primary': '#FFFFFF',
    'user-bubble': '#1B4C86',
    'user-ink': '#FFFFFF',
    focus: '#7FA9D9', // WP-51: visible against dark surfaces and selected controls
    gold: gold[500],
    'gold-soft': '#2A2210',
    'gold-muted': '#3A2E14',
    'gold-ink': gold[500],
    'on-gold': '#2B1F05',
    ok: '#5FD39A',
    'ok-bg': '#0C2A1D',
    warn: '#F2B35B',
    'warn-bg': '#2E1E0A',
    bad: '#F59A92',
    'bad-bg': '#2E1212',
    'dot-ok': tailwindColorsPalette.success[500],
    'dot-work': navy[400],
    'dot-need': gold[500],
    'dot-bad': tailwindColorsPalette.danger[500],
    'dot-idle': '#8A98AB',
    'code-bg': navy[950],
    'code-ink': '#CFE0F5',
    'code-string': navy[200],
    'shadow-sm': '0 1px 2px rgba(16,24,40,.05)',
    'shadow-md': '0 8px 24px -8px rgba(0,0,0,.5)',
    scrim: 'rgba(0,26,51,.35)',
};
/** Color tokens per scheme. Keys are identical in both schemes. */
export const atlasColors = {
    light: lightColors,
    dark: darkColors,
};
/** Scheme-independent tokens: fonts, radii, spacing, type scale, layout, motion. */
export const atlasShared = {
    // Fonts. next/font sets --atlas-font-fraunces / --atlas-font-jetbrains (brand/fonts.ts); the family
    // names are the fallback while that wiring is absent (globals.css already loads Inter and Fraunces).
    'font-sans': 'Inter, system-ui, sans-serif',
    'font-display': 'var(--atlas-font-fraunces, Fraunces), Georgia, serif',
    'font-mono': 'var(--atlas-font-jetbrains, JetBrains Mono), ui-monospace, monospace',
    // Radii
    'radius-xs': '6px', // tags, kbd, focus ring
    'radius-sm': '8px', // avatars, icon buttons, composer tools
    'radius-md': '10px', // buttons, drafts, cost strip, nav rows
    'radius-lg': '12px', // briefing items, panel blocks, Atlas row
    'radius-card': '14px', // cards, start tiles
    'radius-xl': '18px', // composer, user bubble
    'radius-pill': '999px', // pills, chips, suggestion buttons
    // Spacing
    'space-2xs': '4px',
    'space-xs': '6px',
    'space-sm': '8px',
    'space-md': '12px',
    'space-lg': '16px',
    'space-xl': '22px',
    'space-2xl': '28px',
    'space-3xl': '36px',
    // Card anatomy
    'card-pad-x': '16px',
    'card-pad-y': '14px',
    'card-hd-pad-y': '12px',
    'card-gap': '12px',
    // Type scale (size/line-height)
    'text-xs': '11px', // kicker, th, kbd
    'text-sm': '12px', // meta, note, evidence
    'text-md': '13.5px', // buttons, table, draft
    'text-base': '14px', // UI body
    'text-lg': '14.5px', // answers, user bubble
    'text-lede': '15.5px',
    'display-sm': '20px', // panel title
    'display-md': '22px', // totals
    'display-lg': '34px', // greeting (28px under 820px)
    // Layout
    'rail-width': '272px',
    'panel-width': '392px',
    'thread-width': '760px',
    // Motion
    'ease-out': 'cubic-bezier(.2,.7,.2,1)',
    'duration-rise': '.5s',
};
/** Breakpoints from the prototype: panel overlays below `panel`, rail becomes a drawer below `rail`. */
export const atlasBreakpoints = { panel: 1180, rail: 820 };
/** Gold may only back these tokens (Atlas mark and Needs-you). */
export const ATLAS_GOLD_TOKENS = [
    'gold',
    'gold-soft',
    'gold-muted',
    'gold-ink',
    'on-gold',
    'dot-need',
];
/** `atlasVar('ink-2')` → `var(--atlas-ink-2)`. */
export function atlasVar(token) {
    return `var(${ATLAS_CSS_VAR_PREFIX}${token})`;
}
/** Scheme-independent Atlas variables (`--atlas-font-*`, `--atlas-radius-*`, ...). */
export function atlasSharedCssVariables() {
    const out = {};
    for (const [k, v] of Object.entries(atlasShared))
        out[`${ATLAS_CSS_VAR_PREFIX}${k}`] = v;
    return out;
}
/** All Atlas variables for one scheme, e.g. for an inline `style` on a self-contained scope. */
export function atlasCssVariables(scheme, options = {}) {
    const out = options.includeShared === false ? {} : atlasSharedCssVariables();
    for (const [k, v] of Object.entries(atlasColors[scheme]))
        out[`${ATLAS_CSS_VAR_PREFIX}${k}`] = v;
    return out;
}
/**
 * Mantine v7 `cssVariablesResolver`. Emits the `--atlas-*` variables on :root (shared) and per
 * `data-mantine-color-scheme`. Only wire this where Mantine's color scheme is the dark-mode source of truth;
 * the web app today switches on `data-theme`, which atlas.css handles.
 */
export const atlasCssVariablesResolver = () => ({
    variables: atlasSharedCssVariables(),
    light: atlasCssVariables('light', { includeShared: false }),
    dark: atlasCssVariables('dark', { includeShared: false }),
});
